import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  getDocs,
  serverTimestamp,
  deleteDoc,
  doc,
  runTransaction,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { startOfDay, endOfDay } from 'date-fns';

// Virhekoodi jota UI kuuntelee
export const SLOT_TAKEN = 'slot_taken';

// Muodosta deterministinen dokumentti-ID slotille: <calendarId>_<YYYY-MM-DD>_<hour>
function slotDocId(calendarId, date, hour) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${calendarId}_${y}-${m}-${d}_${hour}`;
}

/**
 * Kuuntelee viikon varaukset kalenterille (rajataan kalenteriin + aikaväliin).
 * onChange(items, error) — error on null jos ok.
 */
export function listenReservationsForWeek(calendarId, _companyId, weekStart, weekEnd, onChange) {
  if (!calendarId || !weekStart || !weekEnd) return () => {};
  const q = query(
    collection(db, 'reservations'),
    where('tilantyyppi', '==', calendarId),
    where('päivämäärä', '>=', weekStart),
    where('päivämäärä', '<=', weekEnd)
  );
  const unsub = onSnapshot(
    q,
    snap => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      onChange(items, null);
    },
    err => {
      console.error('listenReservationsForWeek error:', err);
      onChange([], err);
    }
  );
  return unsub;
}

/**
 * Luo varauksen atomisesti transaktiolla.
 * - Deterministinen ID estää kaksoiskappaleet
 * - Jos sama dokumentti on jo olemassa, heitetään virhe { code: SLOT_TAKEN }
 * - Ei vaadi security rules -muutoksia
 */
export async function createReservation({
  date,       // JS Date
  hour,       // number 0-23
  userId,
  firstName,
  lastName,
  companyId,
  calendarId,
  price = 0
}) {
  const id = slotDocId(calendarId, date, hour);
  const ref = doc(db, 'reservations', id);

  const payload = {
    päivämäärä: date,
    kellonaika: hour,
    käyttäjäid: userId,
    etunimi: firstName,
    sukunimi: lastName,
    talonyhtiöid: companyId,
    tilantyyppi: calendarId,
    luontiaika: serverTimestamp(),
    hinta: price
  };

  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (snap.exists()) {
        const err = new Error('slot already taken');
        err.code = SLOT_TAKEN;
        throw err;
      }
      // luodaan dokumentti deterministisellä ID:llä
      tx.set(ref, payload);
    });
  } catch (err) {
    // Välitä eteenpäin UI:lle – UI näyttää popup-viestin vain SLOT_TAKEN-tapauksessa
    throw err;
  }
}

/**
 * Poistaa kirjautuneen käyttäjän varauksen tietylle päivälle ja tunnille.
 * Tukee sekä determinististä ID:tä (nopea polku) että vanhaa satunnais-ID-dataa (fallback-haku).
 */
export async function deleteOwnReservationForHour(userId, date, hour, calendarId) {
  // Yritä ensin deterministisellä ID:llä (uudet varaukset)
  if (calendarId) {
    const id = slotDocId(calendarId, date, hour);
    const ref = doc(db, 'reservations', id);
    const s = await getDoc(ref);
    if (s.exists() && s.data()?.käyttäjäid === userId) {
      await deleteDoc(ref);
      return;
    }
    // muussa tapauksessa pudotaan fallbackiin
  }

  // Fallback: etsitään käyttäjän varaus samalta päivältä ja tunnilta
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);
  const q = query(
    collection(db, 'reservations'),
    where('käyttäjäid', '==', userId),
    where('päivämäärä', '>=', dayStart),
    where('päivämäärä', '<=', dayEnd),
    where('kellonaika', '==', hour)
  );
  const snap = await getDocs(q);
  const deletions = [];
  snap.forEach(docSnap => deletions.push(deleteDoc(docSnap.ref)));
  await Promise.all(deletions);
}
